import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon, SupportStrokeIcon } from "../assets/icons";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useExitToPartnerOrLanding } from "../hooks/useExitToPartnerOrLanding";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

/**
 * Shown when this fund-request intention cannot be taken (already committed
 * or otherwise closed). Support lives in the header; Go to app is the recovery
 * path to the viewer's Home (auth first when needed).
 */
export const RequestAlreadyFulfilledPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { exitToPartnerOrLanding } = useExitToPartnerOrLanding();

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  const requestStatus = session.data?.config?.requestStatus;

  useEffect(() => {
    safeCapture(getEvents().REQUEST_FULFILLED_VIEW, { requestStatus });
  }, [requestStatus]);

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            color={title}
            onIconClick={() => openSupport()}
            icon={
              <SupportStrokeIcon
                size={35}
                colors={[backgroundIcon, backgroundIcon]}
                glyphColor={iconColors[0]}
              />
            }
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <ErrorIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("error.requestUnavailableTitle")}
                highlightColor={title}
                secudnary=""
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>
                {t("error.requestUnavailableMessage")}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => {
                safeCapture(getEvents().EXIT_CLICK, {
                  screen: "request_unavailable",
                });
                exitToPartnerOrLanding();
              }}
            >
              {t("error.goToApp")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
